import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import { assignSpeakerPath, type MeetingScope } from 'src/meetings/scope';
import { UnidentifiedSpeakersWarningModal } from './UnidentifiedSpeakersWarningModal';
import { useGenerateMeetingNoteWithDefaultSource } from './useGenerateMeetingNoteWithDefaultSource';

type Props = {
  scope: MeetingScope;
  meeting: FamilyMeeting;
  unidentifiedCount: number;
  firstSpeakerLabel?: string;
  onDone: () => void;
};

/**
 * Drives note generation from the meetings list now that the confirm-attendees
 * modal has been dropped: when every speaker is identified it generates straight
 * away, otherwise it warns first and lets the adviser continue or go and identify.
 */
export function MeetingNoteGenerationController({
  scope,
  meeting,
  unidentifiedCount,
  firstSpeakerLabel,
  onDone,
}: Props) {
  const navigate = useNavigate();
  const { generate, isGenerating, isResolvingSource } = useGenerateMeetingNoteWithDefaultSource(
    meeting.id,
    !!meeting.hasMeetingAgenda,
  );

  const startedRef = useRef(false);
  const hasWarning = unidentifiedCount > 0;

  useEffect(() => {
    if (hasWarning || startedRef.current || isResolvingSource) return;
    startedRef.current = true;
    void generate().then(onDone);
  }, [hasWarning, isResolvingSource, generate, onDone]);

  if (!hasWarning) return null;

  return (
    <UnidentifiedSpeakersWarningModal
      opened
      count={unidentifiedCount}
      isGenerating={isGenerating}
      onClose={onDone}
      onIdentify={() => {
        onDone();
        if (firstSpeakerLabel) navigate(assignSpeakerPath(scope, meeting.id, firstSpeakerLabel));
      }}
      onContinue={async () => {
        await generate();
        onDone();
      }}
    />
  );
}
